import { compose, prop, propOr, isNil } from 'ramda';
import { createSelector } from 'reselect';

import {
  selectPersistedIsQaTeam,
  selectSuperchargerPromo,
} from '../session/selectors';
import { getEnvironment } from '../environment/selectors';

const QA_ENVIRONMENTS = ['local', 'staging'];

export const selectLocation = prop('location');

export const selectLocationType = compose(prop('type'), selectLocation);
export const selectQuery = compose(propOr({}, 'query'), selectLocation);
export const selectPayload = compose(propOr({}, 'payload'), selectLocation);

export const selectParams = createSelector(
  [selectPayload, selectQuery],
  (payload, query) => {
    // Note: this results in a new object each time so we are required to
    // memoize it with createSelector
    return { product: 'waybetter', ...payload, ...query };
  }
);

export const selectPlainFlag = compose(
  ({ plain }) => !isNil(plain),
  selectQuery
);

// qs.parse gives string values, not booleans - ?is_qa_team=true is the only
// value that counts as on, same as how QA gates other test-only UI. Also
// true once it's ever been persisted for this session (see setIsQaTeam,
// dispatched from InviteLanding's thunk): is_qa_team only ever arrives on
// the invite link, and every later step in the flow builds its next URL
// from scratch (see toPhoneNumber/toGlpWonPay/etc in actions.js), dropping
// any existing query string - without the persisted fallback, qa mode would
// silently turn off the moment a tester clicked past the invite page.
// Always on locally and on staging, so the sandbox number (+999) is there
// without the URL param.
export const selectIsQaTeam = createSelector(
  [selectQuery, selectPersistedIsQaTeam, getEnvironment],
  ({ is_qa_team: isQaTeam }, persisted, { WB_ENV }) =>
    isQaTeam === 'true' || persisted || QA_ENVIRONMENTS.includes(WB_ENV)
);

// The Supercharger pay step's promo code field: { show, code }. Shown with
// ?use_promo_code=true, and also applied by itself with ?code=CODE -
// from this URL, or kept for the session from the link that opened the
// funnel (see rememberSuperchargerPromo, for the same reason as is_qa_team
// above).
export const selectSuperchargerPromoParams = createSelector(
  [selectQuery, selectSuperchargerPromo],
  ({ use_promo_code: usePromoCode, code: promoCode }, persisted) => {
    const code =
      (typeof promoCode === 'string' && promoCode.trim()) ||
      persisted?.code ||
      null;

    return {
      show: usePromoCode === 'true' || !!code || !!persisted?.show,
      code,
    };
  }
);

export const selectPage = compose(
  ({ routesMap, type }) => routesMap[type],
  selectLocation
);

export const isDynamicPage = compose((page) => !!page.dynamic, selectPage);

export const selectPreviousPage = (state) => {
  const { routesMap, prev } = selectLocation(state);
  const previousPage = routesMap[prev.type];

  return previousPage;
};

// `location.prev.pathname` is only ever populated once an in-app navigation
// has happened (redux-first-router leaves it as '' on the initial load), so
// this tells us whether there's actually somewhere to go back to.
export const selectHasPreviousPage = compose(
  (prev) => Boolean(prev?.pathname),
  prop('prev'),
  selectLocation
);

export const selectPageComponent = compose(prop('component'), selectPage);

export const selectPageComponentNamespace = compose(
  prop('componentNamespace'),
  selectPage
);

export const selectPageOptions = compose(prop('options'), selectPage);
